import { ApolloError, useMutation } from '@apollo/client'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import { useCallback } from 'react'
import { questionBeingEditedId } from '../../../apollo/apolloClient'
import { SetKpiV2Input } from '../../../data/gql-gen/questionnaire/graphql'
import { LoggerErrorType } from '../../../data/logger'
import { EntryKpi } from '../../../data/model/questionnaire'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import { captureApolloError } from '../../../utils/HelperFunctions'
import { REMOVE_KPI, SET_KPI, SET_ROW_KPIS } from './Kpi.questionnaire.gql'
import {
  KpiError,
  getKpiError,
  getKpiNotReplacedError,
  getKpiRowsError
} from './kpiErrorMessage'
import type { KpiRowsSaveInput } from './kpiRowsDialogModel'

export type KpiSaveInput = Omit<
  SetKpiV2Input,
  'projectId' | 'waveId' | 'surveyId' | 'entryId'
>

/** Each call resolves to false after showing the error, so a caller can keep its dialog open. */
export const useKpiMutations = (entryId: string) => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const { refetchQuestionnaire } = useGetDraftQuestionnaire()
  const { enqueueSnackbar } = useSnackbar()

  // No onError: with one, Apollo resolves a failed mutation instead of rejecting it.
  const [setKpiMutation, { loading: setting }] = useMutation(SET_KPI, {
    context: { clientName: 'questionnaire' }
  })
  const [removeKpiMutation, { loading: removing }] = useMutation(REMOVE_KPI, {
    context: { clientName: 'questionnaire' }
  })
  const [setRowKpisMutation, { loading: markingRows }] = useMutation(
    SET_ROW_KPIS,
    { context: { clientName: 'questionnaire' } }
  )

  const run = useCallback(
    async (
      mutationName: string,
      describeError: (error: unknown) => KpiError,
      send: () => Promise<unknown>
    ) => {
      questionBeingEditedId(entryId)
      try {
        await send()
        return true
      } catch (error) {
        captureApolloError(
          LoggerErrorType.ApolloMutation,
          mutationName,
          error as ApolloError
        )
        const { message, refetch } = describeError(error)
        enqueueSnackbar(message, { variant: SnackbarVariant.Error })
        if (refetch) {
          refetchQuestionnaire()
        }
        return false
      }
    },
    [entryId, enqueueSnackbar, refetchQuestionnaire]
  )

  const setKpi = useCallback(
    async (input: KpiSaveInput) =>
      !!waveId &&
      run(
        'setKpiV2',
        (error) => getKpiError(error, 'save'),
        () =>
          setKpiMutation({
            variables: {
              input: { ...input, projectId, waveId, surveyId, entryId }
            }
          })
      ),
    [entryId, projectId, run, setKpiMutation, surveyId, waveId]
  )

  /** One KPI per row, or none: the server refuses them all if one row can't be marked. */
  const setRowKpis = useCallback(
    async (
      input: KpiRowsSaveInput,
      rowLabelOf: (rowId: string) => string | undefined
    ) =>
      !!waveId &&
      run(
        'setRowKpisV2',
        (error) => getKpiRowsError(error, rowLabelOf),
        () =>
          setRowKpisMutation({
            variables: {
              input: { ...input, projectId, waveId, surveyId, entryId }
            }
          })
      ),
    [entryId, projectId, run, setRowKpisMutation, surveyId, waveId]
  )

  const sendRemoveKpi = useCallback(
    async (kpiId: string, describeError: (error: unknown) => KpiError) =>
      !!waveId &&
      run('removeKpiV2', describeError, () =>
        removeKpiMutation({
          variables: { input: { projectId, waveId, surveyId, kpiId } }
        })
      ),
    [projectId, removeKpiMutation, run, surveyId, waveId]
  )

  const removeKpi = useCallback(
    async (kpiId: string) =>
      sendRemoveKpi(kpiId, (error) => getKpiError(error, 'remove')),
    [sendRemoveKpi]
  )

  /** Saves a KPI that counts something new, then removes the one it replaces; resolves once the new one is saved, false only if it is not. */
  const replaceKpi = useCallback(
    async (
      replaced: Pick<EntryKpi, 'kpiId' | 'label'>,
      input: KpiSaveInput
    ) => {
      if (!(await setKpi(input))) {
        return false
      }
      sendRemoveKpi(replaced.kpiId, () =>
        getKpiNotReplacedError(replaced.label)
      )
      return true
    },
    [sendRemoveKpi, setKpi]
  )

  return {
    setKpi,
    setRowKpis,
    removeKpi,
    replaceKpi,
    saving: setting || removing || markingRows
  }
}
